'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { toast, Toaster } from 'react-hot-toast';
import { createClient } from '@/src/lib/supabase/client';

const allowedDomain = (
  process.env.NEXT_PUBLIC_ALLOWED_EMAIL_DOMAIN ?? ''
)
  .replace(/^@/, '')
  .toLowerCase();

const LoginClient = () => {
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);

  const errorParam = searchParams.get('error');

  const errorMessages: Record<string, string> = {
    'pending-approval':
      '관리자 승인이 필요합니다. 승인 후 다시 로그인해주세요.',
    'auth-failed': '인증에 실패했습니다. 다시 시도해주세요.',
    session: '세션이 만료되었습니다. 다시 로그인해주세요.',
    'no-code': '인증 코드가 없습니다. 다시 시도해주세요.',
    'missing-email': '이메일 정보를 가져올 수 없습니다.',
    'server-error': '서버 오류가 발생했습니다. 다시 시도해주세요.',
  };

  const handleGoogle = async () => {
    try {
      setLoading(true);
      const supabase = createClient();

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/api/auth/callback`,
          queryParams: {
            prompt: 'select_account',
            ...(allowedDomain ? { hd: allowedDomain } : {}),
          },
        },
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      toast.error('로그인에 실패했습니다. 다시 시도해주세요.');
      console.error('[metown-admin] google login failed', error);
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gscale-50 px-6 py-12 text-gscale-900">
      <Toaster
        toastOptions={{
          position: 'top-center',
          className:
            'bg-gscale-25 text-gscale-900 border border-gscale-200 text-xs',
          success: {
            iconTheme: {
              primary: '#1B3886',
              secondary: '#FEFEFE',
            },
          },
        }}
      />
      <div className="w-full max-w-xl rounded-2xl border border-gscale-200 bg-gscale-25 p-10 shadow-lg">
        <div className="mb-8 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-ultramarine-500">
            Metown Internal
          </p>
          <h1 className="mt-4 text-2xl font-semibold">어드민 로그인</h1>
          <p className="mt-2 text-sm text-gscale-600">
            미타운에서 허가된 계정만 접근할 수 있으며 신규 계정은 관리자 승인이
            필요합니다.
          </p>
        </div>

        {errorParam && errorMessages[errorParam] && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessages[errorParam]}
          </div>
        )}

        <div className="space-y-4">
          <button
            type="button"
            onClick={handleGoogle}
            disabled={loading}
            className="flex w-full items-center justify-center gap-3 rounded-lg bg-rblue-500 px-4 py-3 text-sm font-semibold text-gscale-25 transition hover:bg-rblue-400 disabled:bg-rblue-500/70 disabled:text-gscale-100"
          >
            <span className="text-base">Google로 로그인</span>
            {loading && (
              <span className="animate-pulse text-xs text-gscale-25">
                인증 중…
              </span>
            )}
          </button>

          <p className="mt-8 text-center text-xs text-gscale-500">
            미타운에서 허가된 계정만 사용할 수 있으며, 무단 접근은 기록됩니다.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginClient;
